import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { useBuilding } from "@/api";
import { availableArticlesOptions } from "@/api/functions/communication";
import { Article, ArticleCategory } from "@/types";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "@/styles/Communication.module.scss";
import { Button, Icon, LoadingSpinner, SelectRow } from "@app-components";
import NewsTile from "@/components/communication/NewsTile";
import { useWelcomeCommunicationAccess } from "@/hooks/useWelcomeCommunicationAccess";
import { useLoggedInUser, useRouterStore } from "@/store/zustandStore";
import { useLocalState } from "@/hooks/useLocalState";
import { firebaseTimestampToDate } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { isAfter } from "date-fns";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useBuildingQuery } from "@/components/RouterSetup";

export const Route = createFileRoute("/_portal/news/")({
  component: RouteComponent,
  loader: () => {
    const { queryClient, user } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.ensureQueryData(availableArticlesOptions(user.userId));
  },
});

function RouteComponent() {
  const loggedInUser = useLoggedInUser();
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { hasCommunication, visibleForUsers } = useWelcomeCommunicationAccess();
  const { data: building } = useBuilding();
  const { isLoading, data: articles } = useBuildingQuery<Article[]>(
    availableArticlesOptions(loggedInUser.userId),
  );

  const [categoryFilter, setCategoryFilter] = useState("");
  const [markAsReadTimestamp, setMarkAsReadTimestamp] = useLocalState<
    Date | undefined
  >("markNewsAsReadTimestamp", undefined);

  const hasUnreadArticles = useMemo(() => {
    return articles?.some((article) => {
      if (article.hasRead) return false;
      if (!article.publishedAt) return false;
      if (!markAsReadTimestamp) return true;
      return isAfter(
        firebaseTimestampToDate(article.publishedAt),
        markAsReadTimestamp,
      );
    });
  }, [articles, markAsReadTimestamp]);

  const filteredArticles = useMemo(
    () =>
      (!categoryFilter
        ? articles
        : categoryFilter === ArticleCategory.OPERATIONAL
          ? articles?.filter((a) => a.disregardConsent)
          : articles?.filter((a) => !a.disregardConsent)
      )
        ?.slice() // shallow copy to avoid mutating original
        .sort((a, b) => {
          // Pinned articles always come first
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;

          // If both are pinned or unpinned, sort by lastUpdated/publishedAt
          const aTime =
            a.lastUpdated?.at._seconds ?? a.publishedAt?._seconds ?? 0;
          const bTime =
            b.lastUpdated?.at._seconds ?? b.publishedAt?._seconds ?? 0;

          return bTime - aTime; // Most recent first
        }),
    [categoryFilter, articles],
  );

  if (!hasCommunication) return null;
  return (
    <div>
      <PageBreadcrumbs levels={[{ label: t("SIDEBAR.NEWS"), path: "/news" }]} />
      {!visibleForUsers && (
        <p
          className="banner"
          style={{ marginBottom: "var(--wlcm-spacing-sm)" }}
        >
          <Icon name="info" /> {t("COMMUNICATION.HIDDEN_FOR_USERS_INFO")}
        </p>
      )}
      <div className="pageHeader">
        <h1>{t("PAGES.NEWS.HEADER")}</h1>
        <p>
          {t("PAGES.NEWS.SUBHEADER", { buildingName: building.displayName })}
        </p>
      </div>
      <div className={styles.newsGrid}>
        <div id="news-page--category-filter">
          <SelectRow
            tabs
            label={t("COMMUNICATION.CATEGORY")}
            hideLabel
            name="news-category-filter"
            value={categoryFilter}
            onChange={(filter) => setCategoryFilter(filter as string)}
            options={[
              { value: "", label: t("ALL") },
              {
                value: ArticleCategory.OPERATIONAL,
                label: t("NEWS.CATEGORIES.OPERATIONAL"),
              },
              {
                value: ArticleCategory.DEFAULT,
                label: t("NEWS.CATEGORIES.DEFAULT"),
              },
            ]}
          />
          <AnimatePresence initial={false} mode="wait">
            {hasUnreadArticles && (
              <motion.div
                initial={{
                  opacity: 1,
                  ...(isMobile
                    ? { y: 0, height: "auto" }
                    : { x: 0, width: "auto" }),
                }}
                animate={{
                  opacity: 1,
                  ...(isMobile
                    ? { y: 0, height: "auto" }
                    : { x: 0, width: "auto" }),
                }}
                exit={{
                  opacity: 0,
                  ...(isMobile
                    ? { y: -20, height: 0 }
                    : { x: 20, width: 0, height: 0 }),
                  transition: {
                    duration: 0.2, // Fade/Move duration
                    // Collapse after fade
                    width: { delay: 0.2, duration: 0.2 },
                    height: { delay: 0.2, duration: 0.2 },
                  },
                }}
              >
                <Button
                  secondary
                  icon="done_all"
                  onClick={() => setMarkAsReadTimestamp(new Date())}
                  style={{ textWrap: "nowrap" }}
                >
                  {t("NEWS.MARK_ALL_AS_READ")}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {isLoading ? (
          <div className="center">
            <LoadingSpinner />
          </div>
        ) : filteredArticles && filteredArticles.length > 0 ? (
          filteredArticles.map((article) => (
            <NewsTile key={article.id} article={article} />
          ))
        ) : (
          <div
            style={{
              gridColumn: "1 / -1",
              margin: "var(--wlcm-spacing-lg) 0",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "var(--wlcm-spacing-md)",
              opacity: 0.5,
            }}
          >
            <Icon name="campaign" style={{ fontSize: "5rem" }} />
            <h2 style={{ fontWeight: "normal" }}>
              {t("PAGES.NEWS.PLACEHOLDER")}
            </h2>
          </div>
        )}
      </div>
    </div>
  );
}
